// ./src/components/features/Logbook/LogbookEditDrawer/useLogbookEditForm.ts
//
// All form state, derived values, effects, and event handlers for
// LogbookEditDrawer — extracted so the component only handles rendering.
//
// Correctness invariants maintained here:
// - Hook declarations are unconditional and appear before any early returns
// - useMemo is never used for callbacks that contain side effects (useCallback only)
// - Silent submit failures surface an error instead of being swallowed
// - parseNumericInput is used consistently — no inline parseFloat guards
// - isCheckpoint is an explicit flag alongside isRefuel / isExpense

import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { OpResult } from "@sdflc/api-helpers";

import {
  utcToLocalString,
  localStringToUtc,
  getCurrentLocalDateTimeString,
} from "@/utils/dateHelpers";
import { ApiError } from "@/utils/apiResultHandler";
import { Expense, ExpenseInput } from "@/utils/api/ExpenseService";
import { UploadedFile } from "@/utils/api/UploadedFileService";

import { useUserProfileContext } from "@/contexts/UserProfileContext";
import { useOptionalExpensesContext } from "@/contexts/ExpensesContext";
import { useDashboardContext } from "@/contexts/DashboardContext";

import { calculateTankValues, TankCalculations } from "@/components/features";
import { EXPENSE_TYPES } from "@/config";
import { usePlaceLocation, PlaceInitialValues } from "@/hooks/usePlaceLocation";
import { AddressData } from "@/hooks/useGoogleMapsAutocomplete";

import {
  FormData,
  ExpandedSections,
  PriceCalcMode,
  TankType,
  TANK_TYPE_MAIN,
  TANK_TYPE_ADDL,
  KNOWN_FORM_FIELDS,
  getInitialFormData,
  getInitialExpandedSections,
  fuelInTankToSliderValue,
  serializeFormSnapshot,
  isTireRelatedKindId,
} from "../types";
import { parseNumericInput } from "@/utils/misc";
import type { LogbookEditDrawerProps } from "./LogbookEditDrawer";

// ─────────────────────────────────────────────────────────────────────────────
// Internal helpers (module-private — not exported)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Invoke both error-clearing callbacks, deduplicating when both resolve to the
 * same function reference (happens when no prop overrides are provided and both
 * fall back to expensesContext.clearError).
 */
function clearAllErrors(
  a: (() => void) | undefined,
  b: (() => void) | undefined,
): void {
  a?.();
  if (b && b !== a) b();
}

/**
 * Build PlaceInitialValues from an existing Expense record (edit / duplicate).
 *
 * Note: `addressId` is set to an empty string because the Expense record does
 * not carry the Places API identifier — only the resolved address fields.
 * Downstream consumers of PlaceInitialValues must treat addressId === "" as
 * "address is pre-filled but has no Places ID".
 */
function buildPlaceInitialValuesFromRecord(record: Expense): PlaceInitialValues {
  return {
    whereDone: record.whereDone || "",
    location: record.location || "",
    latitude: record.latitude ?? null,
    longitude: record.longitude ?? null,
    savedPlaceId: record.savedPlaceId ?? null,
    addressData: record.address1
      ? ({
        // addressId is intentionally empty: the Expense stores resolved
        // address components, not the originating Places API place ID.
        addressId: "",
        displayName: null,
        address1: record.address1 || null,
        address2: record.address2 || null,
        city: record.city || null,
        country: record.countryId || null,
        latitude: record.latitude ?? null,
        longitude: record.longitude ?? null,
        postalCode: record.postalCode || null,
        region: record.stateProvince || null,
      } as AddressData)
      : null,
  };
}

/**
 * Build PlaceInitialValues from receipt-scan pre-fill data (create mode).
 */
function buildPlaceInitialValuesFromPrefill(data: Partial<Expense>): PlaceInitialValues {
  return {
    whereDone: data.whereDone || "",
    location: data.location || "",
    latitude: data.latitude ?? null,
    longitude: data.longitude ?? null,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Public return type
// ─────────────────────────────────────────────────────────────────────────────

export interface UseLogbookEditFormReturn {
  // ── State ──
  formData: FormData;
  expandedSections: ExpandedSections;
  attachedFiles: UploadedFile[];
  filePickerOpened: boolean;
  priceCalcMode: PriceCalcMode;

  // ── Loading / submitting ──
  isLoadingRecord: boolean;
  showLoadingSkeleton: boolean;
  isSubmitting: boolean;

  // ── Dirty / error ──
  isDirty: boolean;
  formattedGenericError: string | null | undefined;
  apiFieldErrors: Record<string, string | undefined>;

  // ── Derived ──
  selectedCar: ReturnType<ReturnType<typeof useDashboardContext>["getCarById"]> | null;
  odometerUnit: string;
  hasAdditionalTank: boolean;
  currentTankVolume: number;
  isDifferentCurrency: boolean;
  tankCalculations: TankCalculations;
  breakdownTotal: number;
  isRefuel: boolean;
  isExpense: boolean;
  isCheckpoint: boolean;
  whereDoneLabel: string;
  whereDonePlaceholder: string;
  placeTypes: string[];

  // ── Place location hook (passed through for rendering) ──
  placeLocation: ReturnType<typeof usePlaceLocation>;

  // ── Handlers ──
  handleFieldChange: (field: string | undefined, value: string | number | boolean | string[] | null) => void;
  handleTextInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  handleDateChange: (value: string | null) => void;
  handleOdometerChange: (value: number | string) => void;
  handleVolumeChange: (value: number | string) => void;
  handleRefuelTotalPriceChange: (value: number | string) => void;
  handlePricePerVolumeChange: (value: number | string) => void;
  handleTotalPriceInHcChange: (value: number | string) => void;
  handleTankTypeChange: (value: string) => void;
  handleFuelGradeChange: (name: string | undefined, value: string | null) => void;
  handleVolumeUnitChange: (name: string | undefined, value: string | null) => void;
  handleExpenseTotalPriceChange: (value: number | string) => void;
  handleAmountModeChange: (value: string) => void;
  handleCostWorkChange: (value: number | string) => void;
  handleCostPartsChange: (value: number | string) => void;
  handleTaxChange: (value: number | string) => void;
  handleFeesChange: (value: number | string) => void;
  handleTireSetChange: (name: string, value: string | null) => void;
  handleTankLevelChange: (name: string, value: number) => void;
  handleTankLevelUnknownChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  toggleSection: (section: keyof ExpandedSections) => void;
  handleOpenFilePicker: () => void;
  handleCloseFilePicker: () => void;
  handleAddFiles: (selectedFiles: UploadedFile[]) => void;
  handleRemoveFile: (fileId: string) => void;
  handleReorderFiles: (reorderedFiles: UploadedFile[]) => void;
  handleTypeChange: (value: string) => void;
  handleSubmit: () => Promise<void>;
  setPriceCalcMode: (mode: PriceCalcMode) => void;
}

// ─────────────────────────────────────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────────────────────────────────────

export function useLogbookEditForm(
  props: LogbookEditDrawerProps,
  t: (key: string, opts?: Record<string, unknown>) => string,
): UseLogbookEditFormReturn {
  const {
    opened,
    onClose,
    mode,
    recordId,
    lastRecord,
    initialData,
    onLoad,
    onCreate,
    onUpdate,
    onCreated,
    onUpdated,
    isCreating = false,
    isUpdating = false,
    lastError,
    onClearError,
    initialCarId,
    defaultRecordType = EXPENSE_TYPES.REFUEL,
  } = props;

  const { currency, distanceLabelShort } = useUserProfileContext();
  const { getCarById } = useDashboardContext();

  // ── Mode resolution ──────────────────────────────────────────────────────
  // Explicit prop takes priority; fall back to legacy auto-detect so existing
  // call sites need no changes.

  const resolvedMode = mode ?? (recordId ? "edit" : "create");
  const isEditMode = resolvedMode === "edit";
  const isDuplicateMode = resolvedMode === "duplicate";
  const needsLoad = (isEditMode || isDuplicateMode) && Boolean(recordId);

  // ── Context vs Props resolution ──────────────────────────────────────────

  const expensesContext = useOptionalExpensesContext();

  const loadFn = onLoad ?? expensesContext?.get;
  const createFn = onCreate ?? expensesContext?.create;
  const updateFn = onUpdate ?? expensesContext?.update;
  const creating = isCreating || (expensesContext?.isCreating ?? false);
  const updating = isUpdating || (expensesContext?.isUpdating ?? false);
  const operationError = lastError ?? expensesContext?.lastError ?? null;
  const clearOperationError = onClearError ?? expensesContext?.clearError;

  // Duplicate mode always creates, never updates.
  const isSubmitting = creating || (isEditMode ? updating : false);

  // ── Stable refs for callbacks used in effects ────────────────────────────
  // All refs declared unconditionally before any conditional logic.

  const clearOperationErrorRef = useRef(clearOperationError);
  clearOperationErrorRef.current = clearOperationError;

  const onClearErrorRef = useRef(onClearError);
  onClearErrorRef.current = onClearError;

  const loadFnRef = useRef(loadFn);
  loadFnRef.current = loadFn;

  const tRef = useRef(t);
  tRef.current = t;

  // ── Self-loading state ───────────────────────────────────────────────────

  const [record, setRecord] = useState<Expense | null>(null);
  const [isLoadingRecord, setIsLoadingRecord] = useState(false);
  const [loadError, setLoadError] = useState<ApiError | null>(null);
  const [isReadyToLoad, setIsReadyToLoad] = useState(false);

  // ── Form state ───────────────────────────────────────────────────────────

  const [formData, setFormData] = useState<FormData>(() =>
    getInitialFormData(currency, defaultRecordType),
  );
  const [expandedSections, setExpandedSections] = useState<ExpandedSections>(
    () => getInitialExpandedSections(defaultRecordType),
  );
  const [attachedFiles, setAttachedFiles] = useState<UploadedFile[]>([]);
  const [filePickerOpened, setFilePickerOpened] = useState(false);

  // Refuel-specific UI state
  const [priceCalcMode, setPriceCalcMode] = useState<PriceCalcMode>("total-first");
  const [userChangedFuelGrade, setUserChangedFuelGrade] = useState(false);
  const [userChangedVolumeUnit, setUserChangedVolumeUnit] = useState(false);

  // ── Dirty state tracking ─────────────────────────────────────────────────

  const initialSnapshotRef = useRef<string>("");
  const [isDirty, setIsDirty] = useState(false);
  const [placeDirty, setPlaceDirty] = useState(false);

  // Declared here (unconditionally, with the other refs) to satisfy hook
  // ordering rules — it must not appear mid-file between effect declarations.
  const prevCarIdRef = useRef(formData.carId);

  // ── GPS auto-location decision ───────────────────────────────────────────

  const hasInitialCoordinates = Boolean(
    initialData?.latitude != null && initialData?.longitude != null,
  );
  const shouldAutoLocate = resolvedMode === "create" && !hasInitialCoordinates;

  // ── Derived: car-dependent values ────────────────────────────────────────
  // Declared before effects so effects that depend on selectedCar /
  // hasAdditionalTank can reference them without a "used before declaration" error.

  const selectedCar = useMemo(
    () => (formData.carId ? getCarById(formData.carId) ?? null : null),
    [formData.carId, getCarById],
  );

  const hasAdditionalTank = useMemo(
    () => Boolean(selectedCar?.addlTankVolume && selectedCar.addlTankVolume > 0),
    [selectedCar?.addlTankVolume],
  );

  const currentTankVolume = useMemo(() => {
    if (!selectedCar) return 0;
    if (formData.tankType === TANK_TYPE_ADDL) return selectedCar.addlTankVolume || 0;
    return selectedCar.mainTankVolume || selectedCar.tankVolume || 0;
  }, [selectedCar, formData.tankType]);

  // ── Place / location hook ────────────────────────────────────────────────

  const showLoadingSkeleton = isLoadingRecord && !record;

  const placeLocation = usePlaceLocation({
    enabled: opened && !showLoadingSkeleton,
    autoLocate: shouldAutoLocate,
    includePointType: false,
    onFieldChange: () => setPlaceDirty(true),
  });

  // ════════════════════════════════════════════════════════════════════════
  // Effects
  // ════════════════════════════════════════════════════════════════════════

  // Defer the "ready to load" flag by one animation frame so the drawer
  // open animation isn't blocked by the async fetch.
  useEffect(() => {
    if (opened) {
      const frameId = requestAnimationFrame(() => setIsReadyToLoad(true));
      return () => cancelAnimationFrame(frameId);
    } else {
      setIsReadyToLoad(false);
    }
    return undefined;
  }, [opened]);

  // Load record when opened in edit or duplicate mode.
  useEffect(() => {
    let isCancelled = false;

    if (isReadyToLoad && needsLoad && recordId && loadFnRef.current) {
      setIsLoadingRecord(true);
      setRecord(null);
      setLoadError(null);

      const loadRecord = async () => {
        const result = await loadFnRef.current!(recordId);

        if (isCancelled) return;

        if (result.didSucceed()) {
          setRecord(result.getDataFirst() as Expense);
          setLoadError(null);
        } else {
          setRecord(null);
          setLoadError(
            ApiError.fromResult(result, tRef.current("logbook:form.loadError")),
          );
        }

        setIsLoadingRecord(false);
      };

      loadRecord();
    } else if (!opened) {
      setRecord(null);
      setIsLoadingRecord(false);
      setLoadError(null);
    }

    return () => {
      isCancelled = true;
    };
  }, [isReadyToLoad, opened, needsLoad, recordId]);

  // Sync dirty flag whenever form content changes.
  useEffect(() => {
    if (!opened || showLoadingSkeleton) return;
    const formChanged =
      serializeFormSnapshot(formData, attachedFiles) !== initialSnapshotRef.current;
    setIsDirty(formChanged || placeDirty);
  }, [formData, attachedFiles, placeDirty, opened, showLoadingSkeleton]);

  // ── Form reset on open (or after record loads in edit/duplicate mode) ───
  //
  // Intentional omissions from the dependency array:
  //   - placeLocation.reset   — stable ref returned by usePlaceLocation; adding
  //                             it would cause a loop because reset mutates
  //                             internal hook state.
  //   - onClearErrorRef /
  //     clearOperationErrorRef — accessed via refs; intentionally excluded to
  //                             prevent re-running the reset when the caller
  //                             re-renders and passes a new function identity.
  //
  // If `needsLoad` is true we wait for `record` to be non-null before running,
  // so the record itself (not just needsLoad) drives the effect.
  useEffect(() => {
    if (!opened) return;
    if (needsLoad && !record) return;

    let newFormData: FormData;
    let newFiles: UploadedFile[] = [];
    let placeInitialValues: PlaceInitialValues | undefined;

    if (record) {
      // ── Edit or duplicate mode: populate from loaded record ──────────────
      const tankLevelUnknown = record.fuelInTank == null;
      let tankLevelPercent = 50;
      if (!tankLevelUnknown) {
        tankLevelPercent = record.isFullTank
          ? 100
          : fuelInTankToSliderValue(record.fuelInTank);
      }

      const fileIds = (record.uploadedFiles ?? [])
        .map((f) => f.id)
        .filter((id): id is string => Boolean(id));
      const tagIds = (record.tags ?? [])
        .map((tag) => tag.id)
        .filter((id): id is string => Boolean(id));

      const hasBreakdown =
        (record.costWork ?? 0) > 0 ||
        (record.costParts ?? 0) > 0 ||
        (record.tax ?? 0) > 0 ||
        (record.fees ?? 0) > 0;

      const tankType: TankType =
        record.tankType === TANK_TYPE_ADDL ? TANK_TYPE_ADDL : TANK_TYPE_MAIN;

      newFormData = {
        expenseType: record.expenseType || EXPENSE_TYPES.EXPENSE,
        carId: record.carId || null,
        whenDone:
          utcToLocalString(record.whenDone) || getCurrentLocalDateTimeString(),
        odometer: record.odometer ?? null,
        tankType,
        refuelVolume: record.refuelVolume ?? null,
        volumeEnteredIn: record.volumeEnteredIn || "l",
        totalPrice: record.totalPrice ?? 0,
        pricePerVolume: record.pricePerVolume ?? null,
        fuelGrade: record.fuelGrade || null,
        kindId: record.kindId ?? null,
        shortNote: record.shortNote || "",
        amountMode: hasBreakdown ? "breakdown" : "total",
        costWork: record.costWork ?? null,
        costParts: record.costParts ?? null,
        tax: record.tax ?? null,
        fees: record.fees ?? null,
        tireSetId: record.tireSetId ?? null,
        paidInCurrency: record.paidInCurrency || currency,
        costWorkHc: record.costWorkHc ?? null,
        costPartsHc: record.costPartsHc ?? null,
        totalPriceInHc: record.totalPriceInHc ?? null,
        homeCurrency: record.homeCurrency || currency,
        tankLevelPercent,
        tankLevelUnknown,
        comments: record.comments || "",
        travelId: record.travelId || null,
        tagIds,
        uploadedFilesIds: fileIds,
      };

      newFiles = record.uploadedFiles || [];

      setExpandedSections({
        tankLevel: !tankLevelUnknown,
        travel: Boolean(record.travelId),
        tags: tagIds.length > 0,
        additional: Boolean(record.comments),
        documents: fileIds.length > 0,
      });

      placeInitialValues = buildPlaceInitialValuesFromRecord(record);

      setUserChangedFuelGrade(true);
      setUserChangedVolumeUnit(true);
    } else {
      // ── Create mode ───────────────────────────────────────────────────────
      newFormData = getInitialFormData(currency, defaultRecordType);

      if (initialCarId) {
        newFormData.carId = initialCarId;
      }

      if (lastRecord) {
        newFormData.paidInCurrency = lastRecord.paidInCurrency || currency;
        newFormData.volumeEnteredIn = lastRecord.volumeEnteredIn || "l";
        if (lastRecord.tankType === TANK_TYPE_ADDL) {
          newFormData.tankType = TANK_TYPE_ADDL;
        }
        newFormData.fuelGrade = lastRecord.fuelGrade || null;
        if (!initialCarId && lastRecord.carId) {
          newFormData.carId = lastRecord.carId;
        }
      }

      newFiles = [];

      // Apply pre-fill data (e.g. from receipt scan) on top of defaults.
      if (initialData) {
        if (initialData.expenseType != null)
          newFormData.expenseType = initialData.expenseType;
        if (initialData.carId) newFormData.carId = initialData.carId;
        if (initialData.whenDone)
          newFormData.whenDone =
            utcToLocalString(initialData.whenDone) || newFormData.whenDone;
        if (initialData.odometer != null)
          newFormData.odometer = initialData.odometer;
        if (initialData.refuelVolume != null)
          newFormData.refuelVolume = initialData.refuelVolume;
        if (initialData.volumeEnteredIn)
          newFormData.volumeEnteredIn = initialData.volumeEnteredIn;
        if (initialData.totalPrice != null)
          newFormData.totalPrice = initialData.totalPrice;
        if (initialData.pricePerVolume != null)
          newFormData.pricePerVolume = initialData.pricePerVolume;
        if (initialData.fuelGrade) newFormData.fuelGrade = initialData.fuelGrade;
        if (initialData.kindId != null) newFormData.kindId = initialData.kindId;
        if (initialData.shortNote) newFormData.shortNote = initialData.shortNote;
        if (initialData.paidInCurrency)
          newFormData.paidInCurrency = initialData.paidInCurrency;
        if (initialData.totalPriceInHc != null)
          newFormData.totalPriceInHc = initialData.totalPriceInHc;
        if (initialData.homeCurrency)
          newFormData.homeCurrency = initialData.homeCurrency;
        if (initialData.isFullTank != null) {
          newFormData.tankLevelUnknown = false;
          newFormData.tankLevelPercent = initialData.isFullTank ? 100 : 50;
        }
        if (initialData.fuelInTank != null) {
          newFormData.tankLevelUnknown = false;
          newFormData.tankLevelPercent = fuelInTankToSliderValue(
            initialData.fuelInTank,
          );
        }
        if (initialData.comments) newFormData.comments = initialData.comments;

        if (
          initialData.whereDone ||
          initialData.location ||
          initialData.latitude != null
        ) {
          placeInitialValues = buildPlaceInitialValuesFromPrefill(initialData);
        }

        if (
          Array.isArray(initialData.uploadedFiles) &&
          initialData.uploadedFiles.length
        ) {
          newFormData.uploadedFilesIds = initialData.uploadedFiles
            .map((f) => f.id || "")
            .filter(Boolean);
          newFiles = initialData.uploadedFiles;
        }
      }

      setExpandedSections(getInitialExpandedSections(defaultRecordType));
      setUserChangedFuelGrade(false);
      setUserChangedVolumeUnit(false);
    }

    setFormData(newFormData);
    setAttachedFiles(newFiles);
    initialSnapshotRef.current = serializeFormSnapshot(newFormData, newFiles);
    setIsDirty(false);
    setPlaceDirty(false);
    setPriceCalcMode("total-first");
    setFilePickerOpened(false);

    placeLocation.reset(placeInitialValues);

    clearAllErrors(onClearErrorRef.current, clearOperationErrorRef.current);
  }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [opened, record, lastRecord, initialData, currency, initialCarId, defaultRecordType, needsLoad]);
  //
  // Intentional omissions (see block comment above):
  //   placeLocation.reset, onClearErrorRef, clearOperationErrorRef

  // Auto-fill fuel grade when tank type or car changes.
  useEffect(() => {
    if (userChangedFuelGrade || !selectedCar) return;
    const defaultGrade =
      formData.tankType === TANK_TYPE_ADDL
        ? selectedCar.addlTankDefaultGrade
        : selectedCar.mainTankDefaultGrade;
    if (defaultGrade) {
      setFormData((prev) => ({ ...prev, fuelGrade: defaultGrade }));
    }
  }, [selectedCar, formData.tankType, userChangedFuelGrade]);

  // Auto-fill volume unit when tank type or car changes.
  useEffect(() => {
    if (userChangedVolumeUnit || !selectedCar) return;
    const defaultUnit =
      formData.tankType === TANK_TYPE_ADDL
        ? selectedCar.addlTankVolumeEnteredIn
        : selectedCar.mainTankVolumeEnteredIn;
    if (defaultUnit) {
      setFormData((prev) => ({ ...prev, volumeEnteredIn: defaultUnit }));
    }
  }, [selectedCar, formData.tankType, userChangedVolumeUnit]);

  // Reset tank type to 'main' if the selected car has no additional tank.
  useEffect(() => {
    if (!hasAdditionalTank && formData.tankType === TANK_TYPE_ADDL) {
      setFormData((prev) => ({ ...prev, tankType: TANK_TYPE_MAIN }));
    }
  }, [hasAdditionalTank, formData.tankType]);

  // Refuel price auto-calculation.
  useEffect(() => {
    if (formData.expenseType !== EXPENSE_TYPES.REFUEL) return;
    if (priceCalcMode === "total-first") {
      if (
        formData.totalPrice != null &&
        formData.refuelVolume != null &&
        formData.refuelVolume > 0
      ) {
        const rounded =
          Math.round((formData.totalPrice / formData.refuelVolume) * 1000) / 1000;
        if (formData.pricePerVolume !== rounded) {
          setFormData((prev) => ({ ...prev, pricePerVolume: rounded }));
        }
      }
    } else {
      if (
        formData.pricePerVolume != null &&
        formData.refuelVolume != null &&
        formData.refuelVolume > 0
      ) {
        const rounded =
          Math.round(formData.pricePerVolume * formData.refuelVolume * 100) / 100;
        if (formData.totalPrice !== rounded) {
          setFormData((prev) => ({ ...prev, totalPrice: rounded }));
        }
      }
    }
  }, [
    formData.totalPrice,
    formData.refuelVolume,
    formData.pricePerVolume,
    priceCalcMode,
    formData.expenseType,
  ]);

  // Expense breakdown auto-calculation.
  useEffect(() => {
    if (
      formData.expenseType !== EXPENSE_TYPES.EXPENSE ||
      formData.amountMode !== "breakdown"
    )
      return;
    const hasAny =
      formData.costWork != null ||
      formData.costParts != null ||
      formData.tax != null ||
      formData.fees != null;
    if (!hasAny) return;
    const sum =
      (formData.costWork || 0) +
      (formData.costParts || 0) +
      (formData.tax || 0) +
      (formData.fees || 0);
    const rounded = Math.round(sum * 100) / 100;
    if (formData.totalPrice !== rounded) {
      setFormData((prev) => ({ ...prev, totalPrice: rounded }));
    }
  }, [
    formData.costWork,
    formData.costParts,
    formData.tax,
    formData.fees,
    formData.amountMode,
    formData.totalPrice,
    formData.expenseType,
  ]);

  // Clear tireSetId when kindId changes away from a tire-related kind.
  useEffect(() => {
    if (!isTireRelatedKindId(formData.kindId) && formData.tireSetId) {
      setFormData((prev) => ({ ...prev, tireSetId: null }));
    }
  }, [formData.kindId, formData.tireSetId]);

  // Clear tireSetId when carId changes.
  // prevCarIdRef is declared at the top of the hook with the other refs.
  useEffect(() => {
    if (prevCarIdRef.current !== formData.carId) {
      prevCarIdRef.current = formData.carId;
      if (formData.tireSetId) {
        setFormData((prev) => ({ ...prev, tireSetId: null }));
      }
    }
  }, [formData.carId, formData.tireSetId]);

  // ════════════════════════════════════════════════════════════════════════
  // Derived values (useMemo — pure computations, no side effects)
  // ════════════════════════════════════════════════════════════════════════

  const odometerUnit = useMemo(
    () => selectedCar?.mileageIn || distanceLabelShort,
    [selectedCar?.mileageIn, distanceLabelShort],
  );

  const isDifferentCurrency = useMemo(
    () =>
      Boolean(formData.paidInCurrency) &&
      Boolean(formData.homeCurrency) &&
      formData.paidInCurrency.toUpperCase() !== formData.homeCurrency.toUpperCase(),
    [formData.paidInCurrency, formData.homeCurrency],
  );

  const tankCalculations = useMemo((): TankCalculations => {
    if (formData.tankLevelUnknown) {
      return calculateTankValues(0, 0, 0);
    }
    const refuelVol =
      formData.expenseType === EXPENSE_TYPES.REFUEL ? formData.refuelVolume || 0 : 0;
    return calculateTankValues(
      formData.tankLevelPercent,
      currentTankVolume,
      refuelVol,
    );
  }, [
    formData.tankLevelPercent,
    formData.tankLevelUnknown,
    currentTankVolume,
    formData.refuelVolume,
    formData.expenseType,
  ]);

  const breakdownTotal = useMemo(() => {
    if (formData.amountMode !== "breakdown") return 0;
    const sum =
      (formData.costWork || 0) +
      (formData.costParts || 0) +
      (formData.tax || 0) +
      (formData.fees || 0);
    return Math.round(sum * 100) / 100;
  }, [
    formData.amountMode,
    formData.costWork,
    formData.costParts,
    formData.tax,
    formData.fees,
  ]);

  // Explicit flags for all three record types — avoids implicit "else" branches
  // that would silently misbehave if a fourth type were ever added.
  const isRefuel = formData.expenseType === EXPENSE_TYPES.REFUEL;
  const isExpense = formData.expenseType === EXPENSE_TYPES.EXPENSE;
  const isCheckpoint = !isRefuel && !isExpense;

  const whereDoneLabel = useMemo(() => {
    if (isRefuel) return t("refuels:form.fields.whereDone.label");
    if (isExpense) return t("expenses:form.fields.whereDone.label");
    return t("checkpoints:form.fields.whereDone.label");
  }, [isRefuel, isExpense, t]);

  const whereDonePlaceholder = useMemo(() => {
    if (isRefuel) return t("refuels:form.fields.whereDone.placeholder");
    if (isExpense) return t("expenses:form.fields.whereDone.placeholder");
    return t("checkpoints:form.fields.whereDone.placeholder");
  }, [isRefuel, isExpense, t]);

  const placeTypes = useMemo(
    () => (isRefuel ? ["gas_station"] : ["establishment"]),
    [isRefuel],
  );

  const effectiveError = loadError || operationError;

  const { fields: apiFieldErrors, genericErrors } = useMemo(
    () => ApiError.getFormErrors(effectiveError, KNOWN_FORM_FIELDS),
    [effectiveError],
  );

  const formattedGenericError = useMemo(
    () => ApiError.formatErrors(genericErrors),
    [genericErrors],
  );

  // ════════════════════════════════════════════════════════════════════════
  // Handlers (useCallback — may contain side effects; never useMemo)
  // ════════════════════════════════════════════════════════════════════════

  const handleFieldChange = useCallback(
    (
      field: string | undefined,
      value: string | number | boolean | string[] | null,
    ) => {
      if (!field) return;
      setFormData((prev) => ({ ...prev, [field]: value }));
    },
    [],
  );

  const handleTextInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      handleFieldChange(e.target.name, e.target.value);
    },
    [handleFieldChange],
  );

  const handleDateChange = useCallback(
    (value: string | null) => handleFieldChange("whenDone", value),
    [handleFieldChange],
  );

  const handleOdometerChange = useCallback(
    (value: number | string) => handleFieldChange("odometer", parseNumericInput(value)),
    [handleFieldChange],
  );

  // ── Refuel handlers ──────────────────────────────────────────────────────

  const handleVolumeChange = useCallback(
    (value: number | string) => handleFieldChange("refuelVolume", parseNumericInput(value)),
    [handleFieldChange],
  );

  // useCallback (not useMemo) — contains the setPriceCalcMode side effect.
  const handleRefuelTotalPriceChange = useCallback(
    (value: number | string) => {
      handleFieldChange("totalPrice", parseNumericInput(value));
      if (value !== "" && value !== undefined) {
        setPriceCalcMode("total-first");
      }
    },
    [handleFieldChange],
  );

  // useCallback (not useMemo) — contains the setPriceCalcMode side effect.
  const handlePricePerVolumeChange = useCallback(
    (value: number | string) => {
      handleFieldChange("pricePerVolume", parseNumericInput(value));
      if (value !== "" && value !== undefined) {
        setPriceCalcMode("unit-first");
      }
    },
    [handleFieldChange],
  );

  const handleTotalPriceInHcChange = useCallback(
    (value: number | string) =>
      handleFieldChange("totalPriceInHc", parseNumericInput(value)),
    [handleFieldChange],
  );

  const handleTankTypeChange = useCallback(
    (value: string) => {
      handleFieldChange("tankType", value);
      // Reset "user overrode" flags so the car's defaults re-apply for the
      // newly selected tank (main vs additional).
      setUserChangedFuelGrade(false);
      setUserChangedVolumeUnit(false);
    },
    [handleFieldChange],
  );

  // useCallback (not useMemo) — contains the setUserChangedFuelGrade side effect.
  const handleFuelGradeChange = useCallback(
    (name: string | undefined, value: string | null) => {
      handleFieldChange(name, value);
      setUserChangedFuelGrade(true);
    },
    [handleFieldChange],
  );

  // useCallback (not useMemo) — contains the setUserChangedVolumeUnit side effect.
  const handleVolumeUnitChange = useCallback(
    (name: string | undefined, value: string | null) => {
      handleFieldChange(name, value);
      setUserChangedVolumeUnit(true);
    },
    [handleFieldChange],
  );

  // ── Expense handlers ─────────────────────────────────────────────────────

  const handleExpenseTotalPriceChange = useCallback(
    (value: number | string) => {
      const numValue = parseNumericInput(value);
      setFormData((prev) => {
        const updates: Partial<FormData> = { totalPrice: numValue };
        // When the user edits the total directly while in "total" mode, clear
        // any breakdown that no longer matches the new total.
        if (prev.amountMode === "total") {
          const breakdownSum = Math.round(
            ((prev.costWork || 0) +
              (prev.costParts || 0) +
              (prev.tax || 0) +
              (prev.fees || 0)) *
            100,
          ) / 100;
          if (breakdownSum > 0 && numValue !== breakdownSum) {
            updates.costWork = null;
            updates.costParts = null;
            updates.tax = null;
            updates.fees = null;
          }
        }
        return { ...prev, ...updates };
      });
    },
    [],
  );

  const handleAmountModeChange = useCallback((value: string) => {
    const newMode = value as "total" | "breakdown";
    setFormData((prev) => {
      if (newMode === "total") {
        const sum = Math.round(
          ((prev.costWork || 0) +
            (prev.costParts || 0) +
            (prev.tax || 0) +
            (prev.fees || 0)) *
          100,
        ) / 100;
        return {
          ...prev,
          amountMode: newMode,
          totalPrice: sum > 0 ? sum : prev.totalPrice,
        };
      }
      return { ...prev, amountMode: newMode };
    });
  }, []);

  const handleCostWorkChange = useCallback(
    (value: number | string) => handleFieldChange("costWork", parseNumericInput(value)),
    [handleFieldChange],
  );

  const handleCostPartsChange = useCallback(
    (value: number | string) => handleFieldChange("costParts", parseNumericInput(value)),
    [handleFieldChange],
  );

  const handleTaxChange = useCallback(
    (value: number | string) => handleFieldChange("tax", parseNumericInput(value)),
    [handleFieldChange],
  );

  const handleFeesChange = useCallback(
    (value: number | string) => handleFieldChange("fees", parseNumericInput(value)),
    [handleFieldChange],
  );

  const handleTireSetChange = useCallback(
    (_name: string, value: string | null) => {
      setFormData((prev) => ({ ...prev, tireSetId: value }));
    },
    [],
  );

  // ── Tank level handlers ──────────────────────────────────────────────────

  const handleTankLevelChange = useCallback(
    (_name: string, value: number) => handleFieldChange("tankLevelPercent", value),
    [handleFieldChange],
  );

  const handleTankLevelUnknownChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) =>
      handleFieldChange("tankLevelUnknown", e.currentTarget.checked),
    [handleFieldChange],
  );

  // ── Section / file handlers ───────────────────────────────────────────────

  const toggleSection = useCallback((section: keyof ExpandedSections) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  }, []);

  const handleOpenFilePicker = useCallback(() => setFilePickerOpened(true), []);
  const handleCloseFilePicker = useCallback(() => setFilePickerOpened(false), []);

  const handleAddFiles = useCallback((selectedFiles: UploadedFile[]) => {
    const newIds = selectedFiles
      .map((f) => f.id)
      .filter((id): id is string => Boolean(id));
    setFormData((prev) => ({
      ...prev,
      uploadedFilesIds: [...prev.uploadedFilesIds, ...newIds],
    }));
    setAttachedFiles((prev) => [...prev, ...selectedFiles]);
    setFilePickerOpened(false);
  }, []);

  const handleRemoveFile = useCallback((fileId: string) => {
    setFormData((prev) => ({
      ...prev,
      uploadedFilesIds: prev.uploadedFilesIds.filter((id) => id !== fileId),
    }));
    setAttachedFiles((prev) => prev.filter((f) => f.id !== fileId));
  }, []);

  const handleReorderFiles = useCallback((reorderedFiles: UploadedFile[]) => {
    setAttachedFiles(reorderedFiles);
    const reorderedIds = reorderedFiles
      .map((f) => f.id)
      .filter((id): id is string => Boolean(id));
    setFormData((prev) => ({ ...prev, uploadedFilesIds: reorderedIds }));
  }, []);

  // ── Type change (create mode only) ───────────────────────────────────────

  const handleTypeChange = useCallback((value: string) => {
    const newType = Number(value);
    setFormData((prev) => ({
      ...prev,
      expenseType: newType,
      // Non-refuel types default to "tank level unknown" since fuel tracking
      // is optional for expenses and checkpoints.
      // When switching back to Refuel from "unknown" state, default to full.
      tankLevelUnknown:
        newType !== EXPENSE_TYPES.REFUEL ? true : prev.tankLevelUnknown,
      tankLevelPercent:
        newType === EXPENSE_TYPES.REFUEL && prev.tankLevelUnknown
          ? 100
          : prev.tankLevelPercent,
    }));
    setExpandedSections((prev) => ({
      ...prev,
      tankLevel: newType === EXPENSE_TYPES.REFUEL,
    }));
  }, []);

  // ── Submit ────────────────────────────────────────────────────────────────

  const handleSubmit = useCallback(async () => {
    clearAllErrors(onClearError, clearOperationError);
    setLoadError(null);

    const isTireRelated = isTireRelatedKindId(formData.kindId);
    const isRefuelType = formData.expenseType === EXPENSE_TYPES.REFUEL;
    const isExpenseType = formData.expenseType === EXPENSE_TYPES.EXPENSE;
    // isCheckpoint is the third case: !isRefuelType && !isExpenseType

    const costWork =
      isExpenseType && formData.amountMode === "breakdown" ? formData.costWork : null;
    const costParts =
      isExpenseType && formData.amountMode === "breakdown" ? formData.costParts : null;
    const tax =
      isExpenseType && formData.amountMode === "breakdown" ? formData.tax : null;
    const fees =
      isExpenseType && formData.amountMode === "breakdown" ? formData.fees : null;

    const placeFields = placeLocation.getPlaceFields();

    const submitData: ExpenseInput = {
      expenseType: formData.expenseType,
      carId: formData.carId,
      whenDone: localStringToUtc(formData.whenDone),
      odometer: formData.odometer,
      tankType: isRefuelType ? formData.tankType : null,
      refuelVolume: isRefuelType ? formData.refuelVolume : null,
      volumeEnteredIn: isRefuelType ? formData.volumeEnteredIn : null,
      pricePerVolume: isRefuelType ? formData.pricePerVolume : null,
      fuelGrade: isRefuelType ? formData.fuelGrade?.trim() || null : null,
      kindId: isExpenseType ? formData.kindId : null,
      shortNote: isExpenseType ? formData.shortNote?.trim() || null : null,
      costWork,
      costParts,
      tax,
      fees,
      tireSetId: isExpenseType && isTireRelated ? formData.tireSetId : null,
      // Checkpoints carry no monetary value.
      totalPrice: isCheckpoint ? 0 : formData.totalPrice,
      paidInCurrency:
        isRefuelType || isExpenseType ? formData.paidInCurrency || null : null,
      costWorkHc: isExpenseType && isDifferentCurrency ? formData.costWorkHc : costWork,
      costPartsHc:
        isExpenseType && isDifferentCurrency ? formData.costPartsHc : costParts,
      totalPriceInHc: isDifferentCurrency
        ? formData.totalPriceInHc
        : formData.totalPrice,
      homeCurrency:
        isRefuelType || isExpenseType ? formData.homeCurrency || null : null,
      isFullTank: formData.tankLevelUnknown ? null : tankCalculations.isFullTank,
      fuelInTank: formData.tankLevelUnknown ? null : tankCalculations.fuelInTank,
      remainingInTankBefore:
        isRefuelType && !formData.tankLevelUnknown
          ? tankCalculations.remainingInTankBefore
          : null,
      whereDone: placeFields.whereDone?.trim() || null,
      location: placeFields.location?.trim() || null,
      address1: placeFields.address1 || null,
      address2: placeFields.address2 || null,
      city: placeFields.city || null,
      stateProvince: placeFields.stateProvince || null,
      postalCode: placeFields.postalCode || null,
      countryId: placeFields.countryId || null,
      latitude: placeFields.latitude ?? null,
      longitude: placeFields.longitude ?? null,
      savedPlaceId: placeFields.savedPlaceId || null,
      savePlace: placeFields.savePlace || null,
      comments: formData.comments?.trim() || null,
      travelId: formData.travelId,
      tags: formData.tagIds.length > 0 ? formData.tagIds : null,
      uploadedFilesIds: formData.uploadedFilesIds,
    };

    let result: OpResult | null = null;

    if (isEditMode) {
      if (!updateFn || !recordId) {
        // Should never happen if the drawer is opened correctly in edit mode,
        // but guard explicitly so the failure is visible rather than silent.
        console.error(
          "LogbookEditDrawer: handleSubmit called in edit mode but updateFn or recordId is missing",
        );
        return;
      }
      result = await updateFn(recordId, submitData);
      if (result.didSucceed()) {
        setIsDirty(false);
        const savedRecord = (result.data as Expense[] | undefined)?.[0] ?? null;
        if (savedRecord) onUpdated?.(savedRecord);
        onClose();
      }
    } else {
      // Create mode or duplicate mode — always calls createFn.
      if (!createFn) {
        console.error(
          "LogbookEditDrawer: handleSubmit called in create/duplicate mode but createFn is missing",
        );
        return;
      }
      result = await createFn(submitData);
      if (result.didSucceed()) {
        setIsDirty(false);
        const savedRecord = (result.data as Expense[] | undefined)?.[0] ?? null;
        if (savedRecord) onCreated?.(savedRecord);
        onClose();
      }
    }
  }, [
    formData,
    tankCalculations,
    isDifferentCurrency,
    isEditMode,
    isCheckpoint,
    recordId,
    placeLocation,
    createFn,
    updateFn,
    onClose,
    onClearError,
    clearOperationError,
    onCreated,
    onUpdated,
  ]);

  // ════════════════════════════════════════════════════════════════════════
  // Return
  // ════════════════════════════════════════════════════════════════════════

  return {
    formData,
    expandedSections,
    attachedFiles,
    filePickerOpened,
    priceCalcMode,
    isLoadingRecord,
    showLoadingSkeleton,
    isSubmitting,
    isDirty,
    formattedGenericError,
    apiFieldErrors,
    selectedCar,
    odometerUnit,
    hasAdditionalTank,
    currentTankVolume,
    isDifferentCurrency,
    tankCalculations,
    breakdownTotal,
    isRefuel,
    isExpense,
    isCheckpoint,
    whereDoneLabel,
    whereDonePlaceholder,
    placeTypes,
    placeLocation,
    handleFieldChange,
    handleTextInputChange,
    handleDateChange,
    handleOdometerChange,
    handleVolumeChange,
    handleRefuelTotalPriceChange,
    handlePricePerVolumeChange,
    handleTotalPriceInHcChange,
    handleTankTypeChange,
    handleFuelGradeChange,
    handleVolumeUnitChange,
    handleExpenseTotalPriceChange,
    handleAmountModeChange,
    handleCostWorkChange,
    handleCostPartsChange,
    handleTaxChange,
    handleFeesChange,
    handleTireSetChange,
    handleTankLevelChange,
    handleTankLevelUnknownChange,
    toggleSection,
    handleOpenFilePicker,
    handleCloseFilePicker,
    handleAddFiles,
    handleRemoveFile,
    handleReorderFiles,
    handleTypeChange,
    handleSubmit,
    setPriceCalcMode,
  };
}